import { useMemo } from 'react';
import { Grid } from '@mui/material';

import { HelpTip, Typography, TextField, ToggleButtonGroupField } from '@occc-app/design-system';
import { parseNumber } from '@occc-app/lib/finalFormUtils';
import { OfferFilter } from '@occc/graphql';
import { OfferSelect } from '@occc-app/routes/CampaignComponents/components/OfferSelect';
import { AwardType } from '@occc-app/offerHelpers/types';
import { getButtonsByTypes } from './earning-mechanism-award-selector.utils';
import { EarningMechanismAwardSelectorTitle } from './earning-mechanism-award-selector-title.component';

type EarningMechanismAwardSelectorProps = {
  buttonTypes: AwardType[];
  makeOffersFilter: (search: string) => OfferFilter;
  values: {
    awardType: AwardType;
  };
  fieldNamePrefix?: string;
  awardTypeFieldName?: string;
  awardPointsFieldName?: string;
  awardVoucherFieldName?: string;
  awardVoucherExpirationFieldName?: string;
  awardVoucherExpirationEnabled?: boolean;
  title?: string;
  isTitleVisible?: boolean;
};

export const EarningMechanismAwardSelector = (props: EarningMechanismAwardSelectorProps) => {
  const {
    buttonTypes,
    makeOffersFilter,
    values,
    fieldNamePrefix = '',
    awardTypeFieldName = `${fieldNamePrefix}awardType`,
    awardPointsFieldName = `${fieldNamePrefix}awardPoints.points`,
    awardVoucherFieldName = `${fieldNamePrefix}awardVouchers.voucheredOffer`,
    awardVoucherExpirationFieldName = `${fieldNamePrefix}awardVouchers.voucheredOffer.expirationDays`,
    awardVoucherExpirationEnabled,
    title = 'Give members',
    isTitleVisible = true,
  } = props;

  const buttons = useMemo(() => getButtonsByTypes(buttonTypes), [buttonTypes]);

  const isAwardNothing =
    values?.awardType === AwardType.AwardExternal || values?.awardType === AwardType.AwardNullable;
  const isAwardVouchers = values?.awardType === AwardType.AwardVouchers;

  return (
    <>
      {isTitleVisible && (
        <EarningMechanismAwardSelectorTitle isAwardVouchers={isAwardVouchers} title={title} />
      )}
      <Grid container spacing={2}>
        <Grid item>
          <ToggleButtonGroupField name={awardTypeFieldName} buttons={buttons} />
        </Grid>

        {isAwardNothing && (
          <Grid item xs={12}>
            <HelpTip title='An award of nothing'>
              <Typography variant='body2' color='textSecondary'>
                An award of nothing is useful for tracking interactions or when creating an offer
                fulfillment journey.
              </Typography>
            </HelpTip>
          </Grid>
        )}

        {values?.awardType === AwardType.AwardPoints && (
          <Grid item xs={3}>
            <TextField
              name={awardPointsFieldName}
              label='Number of Points'
              fieldProps={{
                parse: parseNumber,
              }}
            />
          </Grid>
        )}

        {values?.awardType === AwardType.AwardVouchers && (
          <Grid item xs>
            <OfferSelect
              name={awardVoucherFieldName}
              label='Select Award'
              makeOffersFilter={makeOffersFilter}
            />
          </Grid>
        )}
        {values?.awardType === AwardType.AwardVouchers && awardVoucherExpirationEnabled && (
          <Grid item xs={3}>
            <TextField
              name={awardVoucherExpirationFieldName}
              label='Award Expiration Days'
              helperText='Optional'
              fieldProps={{
                parse: parseNumber,
              }}
            />
          </Grid>
        )}
      </Grid>
    </>
  );
};
